import React, { useState } from 'react';
import { FaCalculator, FaWeight, FaRuler, FaDownload } from 'react-icons/fa';
import { generateBMIReport } from '../utils/pdfGenerator';

const Services = () => {
  const [bmiData, setBmiData] = useState({
    weight: '',
    height: '',
    age: '',
    gender: 'male',
    activityLevel: 'moderate'
  });
  const [bmiResult, setBmiResult] = useState(null);

  const calculateBMI = () => {
    const weight = parseFloat(bmiData.weight);
    const height = parseFloat(bmiData.height) / 100;
    
    if (weight && height) {
      const bmi = weight / (height * height);
      const category = getBMICategory(bmi);
      const idealWeight = getIdealWeight(height, bmiData.gender);
      const bmr = calculateBMR(weight, height * 100, bmiData.age, bmiData.gender);
      const calories = calculateCalories(bmr, bmiData.activityLevel);
      
      setBmiResult({
        bmi: bmi.toFixed(1),
        category,
        idealWeight,
        bmr: Math.round(bmr),
        calories: Math.round(calories),
        weightDiff: (weight - idealWeight).toFixed(1)
      });
    }
  };

  const getBMICategory = (bmi) => {
    if (bmi < 18.5) return { text: 'Underweight', color: 'text-blue-400', bg: 'bg-blue-900/50', border: 'border-blue-500' };
    if (bmi < 25) return { text: 'Normal', color: 'text-green-400', bg: 'bg-green-900/50', border: 'border-green-500' };
    if (bmi < 30) return { text: 'Overweight', color: 'text-yellow-400', bg: 'bg-yellow-900/50', border: 'border-yellow-500' };
    return { text: 'Obese', color: 'text-red-400', bg: 'bg-red-900/50', border: 'border-red-500' };
  };

  const getIdealWeight = (height, gender) => {
    const heightCm = height * 100;
    if (gender === 'male') {
      return 52 + 1.9 * ((heightCm - 152.4) / 2.54);
    } else {
      return 49 + 1.7 * ((heightCm - 152.4) / 2.54);
    }
  };

  const calculateBMR = (weight, height, age, gender) => {
    if (gender === 'male') {
      return 10 * weight + 6.25 * height - 5 * age + 5;
    } else {
      return 10 * weight + 6.25 * height - 5 * age - 161;
    }
  };

  const calculateCalories = (bmr, activityLevel) => {
    const multipliers = {
      sedentary: 1.2,
      light: 1.375,
      moderate: 1.55,
      active: 1.725,
      extra: 1.9
    };
    return bmr * multipliers[activityLevel];
  };

  const handleInputChange = (e) => {
    setBmiData({
      ...bmiData,
      [e.target.name]: e.target.value
    });
  };

  const handleDownloadPDF = () => {
    if (bmiResult) {
      generateBMIReport(bmiResult, bmiData);
    }
  };

  return (
    <div className="min-h-screen bg-black py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-yellow-400 uppercase tracking-wide mb-3 sm:mb-4">
            My Services
          </h1>
          <p className="text-gray-400 text-base sm:text-lg">
            Professional fitness services tailored to your goals
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12">
          {[
            {
              title: 'Personal Training',
              price: '₹3,000/month',
              features: ['One-on-one coaching', 'Custom workout plans', 'Form correction', 'Progress tracking'],
              color: 'yellow'
            },
            {
              title: 'Online Coaching',
              price: '₹3,000/month',
              features: ['Video consultations', 'Weekly check-ins', 'Workout videos', '24/7 support'],
              color: 'orange'
            },
            {
              title: 'Nutrition Planning',
              price: '₹1,000/month',
              features: ['Custom meal plans', 'Macro calculations', 'Recipe guides', 'Diet adjustments'],
              color: 'red'
            },
            {
              title: 'Group Training',
              price: '₹1,200/month',
              features: ['Small group sessions', 'Motivational environment', 'Affordable pricing', 'Community support'],
              color: 'yellow'
            },
            {
              title: 'Transformation Program',
              price: '₹5,000/2 months',
              features: ['Complete package', 'Training + Nutrition', 'Weekly progress photos', 'Guaranteed results'],
              color: 'orange'
            },
            {
              title: 'BMI Analysis',
              price: 'Free',
              features: ['Body composition', 'Calorie needs', 'Goal recommendations', 'PDF report'],
              color: 'red'
            }
          ].map((service, index) => (
            <div
              key={index}
              className={`bg-gray-900 border-2 border-${service.color}-500/50 rounded-xl p-6 hover:border-${service.color}-400 transition-all transform hover:scale-105`}
            >
              <h3 className={`text-2xl font-bold text-${service.color}-400 uppercase mb-2`}>
                {service.title}
              </h3>
              <div className="text-3xl font-bold text-white mb-4">{service.price}</div>
              <ul className="space-y-2 mb-6">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="text-gray-400 text-sm flex items-start">
                    <span className={`text-${service.color}-400 mr-2`}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
              <button className={`w-full bg-gradient-to-r from-${service.color}-500 to-${service.color}-600 hover:from-${service.color}-600 hover:to-${service.color}-700 text-black font-bold py-3 rounded-lg uppercase transition-all`}>
                Choose Plan
              </button>
            </div>
          ))}
        </div>

        {/* BMI Calculator Section */}
        <div className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-600 rounded-2xl p-1">
          <div className="bg-black rounded-2xl p-6 sm:p-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-yellow-400 uppercase tracking-wide mb-6 sm:mb-8 text-center">
              Free BMI Calculator
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
              {/* Input Form */}
              <div className="bg-gray-900 rounded-xl p-6 border-2 border-yellow-500/30">
                <h3 className="text-xl font-bold text-yellow-400 uppercase mb-6">Your Details</h3>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-400 font-semibold mb-2 text-sm">
                        <FaWeight className="inline mr-2 text-yellow-400" />
                        Weight (kg)
                      </label>
                      <input
                        type="number"
                        name="weight"
                        value={bmiData.weight}
                        onChange={handleInputChange}
                        placeholder="70"
                        className="w-full px-4 py-3 bg-black border-2 border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 font-semibold mb-2 text-sm">
                        <FaRuler className="inline mr-2 text-orange-400" />
                        Height (cm)
                      </label>
                      <input
                        type="number"
                        name="height"
                        value={bmiData.height}
                        onChange={handleInputChange}
                        placeholder="170"
                        className="w-full px-4 py-3 bg-black border-2 border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-400"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-400 font-semibold mb-2 text-sm">Age</label>
                      <input
                        type="number"
                        name="age"
                        value={bmiData.age}
                        onChange={handleInputChange}
                        placeholder="25"
                        className="w-full px-4 py-3 bg-black border-2 border-gray-700 rounded-lg text-white focus:outline-none focus:border-red-400"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 font-semibold mb-2 text-sm">Gender</label>
                      <select
                        name="gender"
                        value={bmiData.gender}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-black border-2 border-gray-700 rounded-lg text-white focus:outline-none focus:border-red-400"
                      >
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-400 font-semibold mb-2 text-sm">Activity Level</label>
                    <select
                      name="activityLevel"
                      value={bmiData.activityLevel}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-black border-2 border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    >
                      <option value="sedentary">Sedentary (little/no exercise)</option>
                      <option value="light">Light (1-3 days/week)</option>
                      <option value="moderate">Moderate (3-5 days/week)</option>
                      <option value="active">Active (6-7 days/week)</option>
                      <option value="extra">Extra Active (physical job)</option>
                    </select>
                  </div>

                  <button
                    onClick={calculateBMI}
                    className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-black font-bold py-4 rounded-lg uppercase tracking-wide transition-all flex items-center justify-center space-x-2"
                  >
                    <FaCalculator />
                    <span>Calculate BMI</span>
                  </button>
                </div>
              </div>

              {/* Results */}
              <div className="bg-gray-900 rounded-xl p-6 border-2 border-orange-500/30">
                {bmiResult ? (
                  <div>
                    <h3 className="text-xl font-bold text-orange-400 uppercase mb-6">Your Results</h3>
                    
                    <div className={`${bmiResult.category.bg} border-2 ${bmiResult.category.border} rounded-xl p-6 mb-6 text-center`}>
                      <div className="text-6xl font-bold text-white mb-2">{bmiResult.bmi}</div>
                      <div className={`text-xl font-bold ${bmiResult.category.color} uppercase`}>
                        {bmiResult.category.text}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-black border border-gray-700 rounded-lg p-4">
                        <div className="text-gray-400 text-sm mb-1">Ideal Weight</div>
                        <div className="text-2xl font-bold text-yellow-400">{bmiResult.idealWeight.toFixed(1)} kg</div>
                      </div>
                      <div className="bg-black border border-gray-700 rounded-lg p-4">
                        <div className="text-gray-400 text-sm mb-1">Daily Calories</div>
                        <div className="text-2xl font-bold text-orange-400">{bmiResult.calories} cal</div>
                      </div>
                      <div className="bg-black border border-gray-700 rounded-lg p-4">
                        <div className="text-gray-400 text-sm mb-1">BMR</div>
                        <div className="text-2xl font-bold text-red-400">{bmiResult.bmr} cal</div>
                      </div>
                    </div>

                    <button
                      onClick={handleDownloadPDF}
                      className="w-full mt-6 bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-lg uppercase transition-all flex items-center justify-center space-x-2"
                    >
                      <FaDownload />
                      <span>Download Report</span>
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center text-gray-500">
                      <FaCalculator className="text-6xl mx-auto mb-4 opacity-50" />
                      <p className="text-lg">Enter your details and calculate your BMI</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;
